/* eslint-disable react-hooks/rules-of-hooks */
import { HTTP_STATUS } from "@api/constant";
import ArticleCard from "@blocks/Card/ArticleCard";
import { LOCALHOST } from "@config/MetaTagsConstants";
import MainBlock from "@container/MainBlock";
import MainContainer, { AdsSettings } from "@container/MainContainer";
import SideBlock from "@container/SideBlock/SideBlock";
import WrapperBlock from "@container/WrapperBlock";
import { RenderImu1AdInfiniteScroll } from "@elements/Advertisement/RenderImu1AdInfiniteScroll";
import fetchTMApi from "@helper/fetchTMApi";
import { GADataProps, getGTMData } from "@helper/getGtmData";
import { displayAfter, hideAfter } from "@helper/utils";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import {
  CreateLoadMoreHandler,
  type CreateLoadMoreHandlerProps,
} from "@hooks/useLoadMore/CreateLoadMoreHandler";
import LoadMore from "@hooks/useLoadMore/LoadMore";
import useSetFirstRenderedArticleIds from "@hooks/useLoadMore/SetFirstRenderedArticleIds";
import { Route } from "@route-types/Section/+types/Section";
import ErrorPage from "@routes/ErrorPage";
import NotFound from "@routes/NotFound";
import { fetchSectionData } from "@routes/Section/Section.server";
import type {
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import { createMetaTags, MetaTagsProps } from "@sphtech/web2-seo-schema";
import { type ProcessedArticleData } from "@transformer/useOsResponse";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { MoreArticlesRequestData } from "@typings/MoreArticlesApi";
import { SectionPayloadResponseType } from "@typings/PagePayloadType";
import { Fragment, ReactElement, useState } from "react";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

const PAGE_SIZE = 5;
const INITIAL_ARTICLE_COUNT = 5;

export function meta(args: Route.MetaArgs) {
  const dataLoaderResponse = args.loaderData as TRouteWithRedirect<
    SectionPayloadResponseType,
    string
  >;
  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const metaData = dataLoaderResponse.payload.metaTags as MetaTagsProps;
    return createMetaTags(metaData);
  }
}

export default function Section(): ReactElement {
  const dataLoaderResponse: TRouteWithoutRedirect<
    SectionPayloadResponseType,
    string
  > = useLoaderData();

  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }
  const sectionArticles = dataLoaderResponse.payload.data;
  const epaperCoverImageUrl =
    dataLoaderResponse.payload.epaperCoverImage.coverImageUri || "";

  const adsSettings: AdsSettings = useGlobalAdSetting();
  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>(),
  );
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);

  const showLoadMore: boolean = sectionArticles.length == INITIAL_ARTICLE_COUNT;
  useSetFirstRenderedArticleIds(sectionArticles, setRenderedArticleIds);

  const handleLoadMore = async () => {
    const lastLoadedArticle =
      loadMoreData.length == 0 ? sectionArticles.at(-1) : loadMoreData.at(-1);
    const requestData: MoreArticlesRequestData = {
      filterArticlesBasedOn: sectionArticles[0].sectionName,
      sort: lastLoadedArticle && lastLoadedArticle.sort,
    };

    const fetchMoreStoriesData = async (
      requestData: MoreArticlesRequestData,
    ): Promise<ProcessedArticleData[] | []> => {
      try {
        const queryParams = new URLSearchParams({
          section: requestData.filterArticlesBasedOn as string,
          sort: Array.isArray(requestData.sort)
            ? requestData.sort.join(",")
            : "",
          size: "5",
        }).toString();
        const response: ProcessedArticleData[] = await fetchTMApi(
          `more-section-stories?${queryParams}`,
          "GET",
        );

        return response;
      } catch (error) {
        return [];
      }
    };
    const createLoadMoreProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction: fetchMoreStoriesData,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      loadMoreData,
      renderedArticleIds,
      lastLoadedArticle,
      pageSize: PAGE_SIZE,
      pageNumber,
    };
    const response = await CreateLoadMoreHandler(createLoadMoreProps);
    return response;
  };

  const sectionName = dataLoaderResponse.payload.section;

  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;

  return (
    <MainContainer
      sectionName={sectionName}
      pageAdTargetValue={pageAdTargetValue}
      suppliedAdsSettings={adsSettings}
    >
      <WrapperBlock className="flex flex-col lg:flex-row justify-between">
        <MainBlock sectionName={sectionName}>
          {[...sectionArticles, ...loadMoreData].map((section, index) => (
            <Fragment key={section.id}>
              <ArticleCard
                displayWidth={260}
                displayHeight={192}
                elements={{ hasDate: true }}
                {...section}
                overrideStyle={{
                  cardWrapper:
                    (displayAfter(index + 1, PAGE_SIZE)
                      ? "border-none"
                      : "flex-col sm:flex-row") +
                    (hideAfter(index, PAGE_SIZE)
                      ? " sm:flex-row flex-row-reverse"
                      : ""),
                  titleStyle: hideAfter(index, PAGE_SIZE)
                    ? "font-semibold text-2sm text-gray-100 hover:text-gray-100 sm:text-3md sm:text-red-100 sm:hover:text-red-200"
                    : "text-md sm:text-3md",
                  paragraphStyle: hideAfter(index, PAGE_SIZE)
                    ? "hidden sm:block"
                    : "",
                  imageWrapperStyle: hideAfter(index, PAGE_SIZE)
                    ? "w-31.5 h-23 sm:h-auto"
                    : "",
                  imageStyle: hideAfter(index, PAGE_SIZE)
                    ? "h-23 sm:h-auto"
                    : "",
                }}
                refMedium={sectionName}
              />
              {displayAfter(index + 1, PAGE_SIZE) && (
                <>
                  {RenderImu1AdInfiniteScroll(
                    (index + 1) / PAGE_SIZE,
                    sectionName,
                    pageAdTargetValue,
                  )}
                </>
              )}
            </Fragment>
          ))}
          {showLoadMore ? (
            <div className="flex w-full justify-center mb-xl">
              <LoadMore
                rootClassName="my-4 w-full"
                onLoadMore={handleLoadMore}
                hasMore={hasMoreStories}
                loadText="Load More"
                mobileInfiniteScroll={true}
                pageNumber={pageNumber}
                sectionName={sectionName}
              />
            </div>
          ) : null}
        </MainBlock>
        <SideBlock
          sectionName={sectionName}
          className="w-full lg:block"
          pageAdTargetValue={pageAdTargetValue}
          hideAdForMobile={true}
          promoteMMEpaper={sectionName === "maanavar-murasu"}
          epaperCoverImageUrl={epaperCoverImageUrl}
        />
      </WrapperBlock>
    </MainContainer>
  );
}

export async function loader({ request }: Route.LoaderArgs) {
  const url = new URL(request.url, LOCALHOST);
  const sectionName: string[] = url.pathname.split("/").filter(Boolean);
  const section = sectionName[0];

  const data = (await fetchSectionData(section || "")) as TRouteWithRedirect<
    SectionPayloadResponseType,
    string
  >;

  if (data.statusCode !== HTTP_STATUS.SUCCESS) return { ...data };

  const gaData = data.payload.gaData;

  return {
    ...data,
    gtm: getGTMData(gaData as GADataProps),
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<SectionPayloadResponseType | null, string>
  >;
}
